/**
 * 新建参考线对话框（「视图→新建参考线…」，DESIGN.md §6.5 补充，参照 Photoshop）。
 */
import type { JSX } from 'solid-js';
import { Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import styles from './NewGuideDialog.module.css';

export interface NewGuideDialogProps {
  readonly open: boolean;
  /** 画布尺寸，用于给一个居中的默认位置。 */
  readonly documentSize: { readonly width: number; readonly height: number };
  readonly onClose: () => void;
  readonly onConfirm: (axis: 'horizontal' | 'vertical', position: number) => void;
}

export function NewGuideDialog(props: NewGuideDialogProps): JSX.Element {
  const [axis, setAxis] = createSignal<'horizontal' | 'vertical'>('horizontal');
  const [position, setPosition] = createSignal(0);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  // 每次打开都重置成画布正中——跟 Photoshop 弹出这个对话框时的默认值一致
  createEffect(() => {
    if (props.open) {
      setAxis('horizontal');
      setPosition(Math.round(props.documentSize.height / 2));
    }
  });

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="new-guide-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="新建参考线"
          data-testid="new-guide-dialog"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <h2 class={styles.title}>新建参考线</h2>

          <div class={styles.row}>
            <span class={styles.label}>方向</span>
            <div class={styles.radioGroup}>
              <label class={styles.radioOption}>
                <input
                  type="radio"
                  name="new-guide-axis"
                  checked={axis() === 'horizontal'}
                  data-testid="new-guide-axis-horizontal"
                  onChange={() => {
                    setAxis('horizontal');
                    setPosition(Math.round(props.documentSize.height / 2));
                  }}
                />
                水平
              </label>
              <label class={styles.radioOption}>
                <input
                  type="radio"
                  name="new-guide-axis"
                  checked={axis() === 'vertical'}
                  data-testid="new-guide-axis-vertical"
                  onChange={() => {
                    setAxis('vertical');
                    setPosition(Math.round(props.documentSize.width / 2));
                  }}
                />
                垂直
              </label>
            </div>
          </div>

          <label class={styles.row}>
            <span class={styles.label}>位置</span>
            <input
              type="number"
              class={styles.number}
              min="0"
              value={position()}
              data-testid="new-guide-position"
              onInput={(event) => {
                setPosition(Math.max(0, Math.round(Number(event.currentTarget.value) || 0)));
              }}
            />
            <span class={styles.value}>px</span>
          </label>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="new-guide-confirm"
              onClick={() => {
                props.onConfirm(axis(), position());
              }}
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
